/**
 * TanStack Query hooks for content management.
 *
 * All content is site-scoped. Each hook requires a siteSlug parameter.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { postsApi, pagesApi, mediaApi, contentItemsApi, MediaListParams } from '../api';
import type { Post, Page, PageEditorMode, ListParams, ContentData, MediaFolder } from '../types';
import { isScheduleNotRecorded } from '../utils/scheduledPublication';
import { contentTypeKeys } from './useContentTypes';

// ============================================================================
// Query Keys
// ============================================================================

export const contentKeys = {
  posts: {
    all: (siteSlug: string) => ['sites', siteSlug, 'posts'] as const,
    lists: (siteSlug: string) => [...contentKeys.posts.all(siteSlug), 'list'] as const,
    list: (siteSlug: string, params: ListParams) => [...contentKeys.posts.lists(siteSlug), params] as const,
    details: (siteSlug: string) => [...contentKeys.posts.all(siteSlug), 'detail'] as const,
    detail: (siteSlug: string, id: string) => [...contentKeys.posts.details(siteSlug), id] as const,
  },
  pages: {
    all: (siteSlug: string) => ['sites', siteSlug, 'pages'] as const,
    lists: (siteSlug: string) => [...contentKeys.pages.all(siteSlug), 'list'] as const,
    list: (siteSlug: string, params: ListParams) => [...contentKeys.pages.lists(siteSlug), params] as const,
    details: (siteSlug: string) => [...contentKeys.pages.all(siteSlug), 'detail'] as const,
    detail: (siteSlug: string, id: string) => [...contentKeys.pages.details(siteSlug), id] as const,
  },
  media: {
    all: (siteSlug: string) => ['sites', siteSlug, 'media'] as const,
    lists: (siteSlug: string) => [...contentKeys.media.all(siteSlug), 'list'] as const,
    list: (siteSlug: string, params: ListParams) => [...contentKeys.media.lists(siteSlug), params] as const,
    details: (siteSlug: string) => [...contentKeys.media.all(siteSlug), 'detail'] as const,
    detail: (siteSlug: string, id: string) => [...contentKeys.media.details(siteSlug), id] as const,
  },
};

// ============================================================================
// Posts Hooks
// ============================================================================

/** Fetch posts list with pagination */
export function usePosts(siteSlug: string, params: ListParams = {}) {
  return useQuery({
    queryKey: contentKeys.posts.list(siteSlug, params),
    queryFn: () => postsApi.list(siteSlug, params),
    enabled: !!siteSlug,
  });
}

/** Fetch a single post by ID */
export function usePost(siteSlug: string, id: string) {
  return useQuery({
    queryKey: contentKeys.posts.detail(siteSlug, id),
    queryFn: () => postsApi.get(siteSlug, id),
    enabled: !!siteSlug && !!id,
  });
}

/** Create a new post */
export function useCreatePost(siteSlug: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: ContentData<Post>) => postsApi.create(siteSlug, data),
    onError: (error) => {
      if (isScheduleNotRecorded(error)) void queryClient.invalidateQueries({ queryKey: contentKeys.posts.all(siteSlug) });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: contentKeys.posts.all(siteSlug) });
    },
  });
}

/** Update an existing post */
export function useUpdatePost(siteSlug: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: ContentData<Post> }) =>
      postsApi.update(siteSlug, id, data),
    onError: (error) => {
      if (isScheduleNotRecorded(error)) void queryClient.invalidateQueries({ queryKey: contentKeys.posts.all(siteSlug) });
    },
    onSuccess: (response, variables) => {
      queryClient.setQueryData(contentKeys.posts.detail(siteSlug, variables.id), response);
      queryClient.invalidateQueries({ queryKey: contentKeys.posts.all(siteSlug) });
      queryClient.invalidateQueries({
        queryKey: contentKeys.posts.detail(siteSlug, variables.id),
      });
    },
  });
}

/** Delete a post */
export function useDeletePost(siteSlug: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => postsApi.delete(siteSlug, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: contentKeys.posts.all(siteSlug) });
    },
  });
}

// ============================================================================
// Pages Hooks
// ============================================================================

/** Fetch pages list with pagination */
export function usePages(siteSlug: string, params: ListParams = {}) {
  return useQuery({
    queryKey: contentKeys.pages.list(siteSlug, params),
    queryFn: () => pagesApi.list(siteSlug, params),
    enabled: !!siteSlug,
  });
}

/** Fetch a single page by ID */
export function usePage(siteSlug: string, id: string) {
  return useQuery({
    queryKey: contentKeys.pages.detail(siteSlug, id),
    queryFn: () => pagesApi.get(siteSlug, id),
    enabled: !!siteSlug && !!id,
  });
}

/** Create a new page */
export function useCreatePage(siteSlug: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: ContentData<Page>) => pagesApi.create(siteSlug, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: contentKeys.pages.all(siteSlug) });
    },
  });
}

/** Update an existing page */
export function useUpdatePage(siteSlug: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Parameters<typeof pagesApi.update>[2] }) =>
      pagesApi.update(siteSlug, id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: contentKeys.pages.all(siteSlug) });
      queryClient.invalidateQueries({
        queryKey: contentKeys.pages.detail(siteSlug, variables.id),
      });
    },
  });
}

/** Delete a page */
export function useDeletePage(siteSlug: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => pagesApi.delete(siteSlug, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: contentKeys.pages.all(siteSlug) });
    },
  });
}

/** Preview a Tera template with full SSG context */
export function usePreviewTeraPage(siteSlug: string) {
  return useMutation({
    mutationFn: (template: string) => pagesApi.previewTera(siteSlug, template),
  });
}

/** Data for full page preview */
export interface FullPreviewData {
  title: string;
  content: string;
  slug?: string;
  editor_mode?: PageEditorMode;
  template?: string | null;
  render_only?: boolean;
  custom_css?: string;
  custom_js?: string;
  show_title?: boolean;
}

/** Preview a full page with base.html wrapper */
export function usePreviewFullPage(siteSlug: string) {
  return useMutation({
    mutationFn: (data: FullPreviewData) => pagesApi.previewFull(siteSlug, data),
  });
}

/** Data for full post preview */
export interface PostPreviewData {
  title: string;
  content: string;
  slug?: string;
  featured_image?: string;
  featured_image_alt?: string;
  featured_image_caption?: string;
}

/** Preview a full post with base.html wrapper */
export function usePreviewFullPost(siteSlug: string) {
  return useMutation({
    mutationFn: (data: PostPreviewData) => postsApi.previewFull(siteSlug, data),
  });
}

// ============================================================================
// Media Hooks
// ============================================================================

/** Fetch media list with pagination and folder filter */
export function useMedia(siteSlug: string, params: MediaListParams = {}) {
  return useQuery({
    queryKey: contentKeys.media.list(siteSlug, params),
    queryFn: () => mediaApi.list(siteSlug, params),
    enabled: !!siteSlug,
    // Refresh at the next server deadline so an open library drops expired cards.
    refetchInterval: (query) => {
      const deadlines = query.state.data?.data?.flatMap((item) => item.expires_at ? [item.expires_at * 1000] : []) ?? [];
      return deadlines.length ? Math.max(1000, Math.min(...deadlines) - Date.now()) : false;
    },
  });
}

/** Fetch a single media item by ID */
export function useMediaItem(siteSlug: string, id: string) {
  return useQuery({
    queryKey: contentKeys.media.detail(siteSlug, id),
    queryFn: () => mediaApi.get(siteSlug, id),
    enabled: !!siteSlug && !!id,
  });
}

/** Upload a media file to a specific folder */
export function useUploadMedia(siteSlug: string, folder: MediaFolder = 'root') {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (file: File) => mediaApi.upload(siteSlug, file, folder),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: contentKeys.media.all(siteSlug) });
    },
  });
}

/** Delete a media item */
export function useDeleteMedia(siteSlug: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => mediaApi.delete(siteSlug, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: contentKeys.media.all(siteSlug) });
    },
  });
}

/** Delete multiple media items */
export function useDeleteMediaBulk(siteSlug: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (ids: string[]) => mediaApi.deleteBulk(siteSlug, ids),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: contentKeys.media.all(siteSlug) });
    },
  });
}

/** Rename a media item */
export function useRenameMedia(siteSlug: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) =>
      mediaApi.rename(siteSlug, id, name),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: contentKeys.media.all(siteSlug) });
    },
  });
}

// ============================================================================
// Generic Content Item Hooks (for custom types)
// ============================================================================

/** Query key factory for content items */
export const contentItemKeys = {
  all: (siteSlug: string, typeSlug: string) => ['sites', siteSlug, 'content', typeSlug] as const,
  lists: (siteSlug: string, typeSlug: string) => [...contentItemKeys.all(siteSlug, typeSlug), 'list'] as const,
  list: (siteSlug: string, typeSlug: string, params: ListParams) => [...contentItemKeys.lists(siteSlug, typeSlug), params] as const,
  details: (siteSlug: string, typeSlug: string) => [...contentItemKeys.all(siteSlug, typeSlug), 'detail'] as const,
  detail: (siteSlug: string, typeSlug: string, id: string) => [...contentItemKeys.details(siteSlug, typeSlug), id] as const,
};

/** Fetch content items for a content type */
export function useContentItems(siteSlug: string, typeSlug: string, params: ListParams = {}) {
  return useQuery({
    queryKey: contentItemKeys.list(siteSlug, typeSlug, params),
    queryFn: () => contentItemsApi.list(siteSlug, typeSlug, params),
    enabled: !!siteSlug && !!typeSlug,
  });
}

/** Fetch a single content item by ID */
export function useContentItem(siteSlug: string, typeSlug: string, id: string) {
  return useQuery({
    queryKey: contentItemKeys.detail(siteSlug, typeSlug, id),
    queryFn: () => contentItemsApi.get(siteSlug, typeSlug, id),
    enabled: !!siteSlug && !!typeSlug && !!id,
  });
}

/** Create a new content item */
export function useCreateContentItem(siteSlug: string, typeSlug: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Record<string, unknown>) => contentItemsApi.create(siteSlug, typeSlug, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: contentItemKeys.all(siteSlug, typeSlug) });
      // Also update content type item count
      queryClient.invalidateQueries({ queryKey: contentTypeKeys.all(siteSlug) });
    },
  });
}

/** Update a content item */
export function useUpdateContentItem(siteSlug: string, typeSlug: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) =>
      contentItemsApi.update(siteSlug, typeSlug, id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: contentItemKeys.all(siteSlug, typeSlug) });
      queryClient.invalidateQueries({
        queryKey: contentItemKeys.detail(siteSlug, typeSlug, variables.id),
      });
    },
  });
}

/** Delete a content item */
export function useDeleteContentItem(siteSlug: string, typeSlug: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => contentItemsApi.delete(siteSlug, typeSlug, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: contentItemKeys.all(siteSlug, typeSlug) });
      // Also update content type item count
      queryClient.invalidateQueries({ queryKey: contentTypeKeys.all(siteSlug) });
    },
  });
}
